import { Colors } from "@blueprintjs/core";
import styled from "@emotion/styled";

import { EmbedSvg } from "@/components/helpers/ui/EmbedSvg";
import { Box, Flex } from "@/components/layout/flexbox";
import { breakpoints } from "@/components/layout/Media";
import { Container } from "@/components/pieces/bootstrap";
import {
  StyledSubtitle,
  StyledTitle,
} from "@/components/pieces/marketing/Titles";
import { LPLink } from "@/components/pieces/upsell/LPLink";
import { BRAND_PURPLE, PURPLE_TEXT_COLOR } from "@/css/constants";
import { MarketingPageBenefits } from "@/types/marketing";

interface BenefitGridProps {
  items: MarketingPageBenefits["items"];
}

function BenefitGrid({ items }: BenefitGridProps) {
  return (
    <Grid>
      {items.map((item, i) => {
        const { title, photo, description, bgColor } = item;
        return (
          <StyledBox bg={Colors.WHITE} key={"grid-" + i}>
            <Box
              backgroundColor={bgColor}
              width={36}
              position="relative"
              mr={24}
            >
              <StyledIcon>
                <EmbedSvg width={48} height={48} src={photo} />
              </StyledIcon>
            </Box>
            <Flex px={3} py={"32px"} flex={1} flexDirection="column">
              <Box css={{ fontSize: 24, fontWeight: 600, marginBottom: 2 }}>
                {title}
              </Box>
              <Box>{description}</Box>
            </Flex>
          </StyledBox>
        );
      })}
    </Grid>
  );
}

interface BenefitProps {
  title: string;
  subtitle: string;
  benefits: MarketingPageBenefits["items"];
  ctaText?: string;
  ctaLink?: string;
  onCtaClick?: () => void;
}

export function Benefits({
  title,
  subtitle,
  benefits,
  ctaText,
  ctaLink,
  onCtaClick,
}: BenefitProps) {
  return (
    <Box color={PURPLE_TEXT_COLOR} py={80}>
      <Container>
        <Box mb={4}>
          <StyledTitle>{title}</StyledTitle>
          <StyledSubtitle>{subtitle}</StyledSubtitle>
        </Box>
        <BenefitGrid items={benefits} />
        {ctaText ? (
          <Flex alignItems="center" flexDirection="column" mt={5}>
            <Box mb={3}>
              <LPLink
                onCtaClick={onCtaClick}
                to={ctaLink}
                color={BRAND_PURPLE}
                text={ctaText}
                style={{
                  width: 300,
                }}
              />
            </Box>
          </Flex>
        ) : null}
      </Container>
    </Box>
  );
}

const Grid = styled(Box)`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-gap: 24px;
  font-size: 18px;

  @media screen and (max-width: ${breakpoints.md}px) {
    grid-template-columns: repeat(1, 1fr);
  }
`;

const StyledIcon = styled(Box)`
  width: 60px;
  height: 60px;
  left: 12px;
  position: absolute;
  top: 60px;
  transform: translateY(-50%);
`;

const StyledBox = styled(Flex)`
  border: 0.5px solid #e2e2e2;
  box-shadow: 0px 0px 5px rgba(0, 0, 0, 0.05);
  border-radius: 10px;
  overflow: hidden;
  position: relative;
`;
